/**
 * O5 — AccoladeFlash: transient hero card surfaced at high-drama
 * moments. Renders only when `visibleAccoladeAt(...)` returns non-null
 * for the current tick.
 *
 * Visual signature: large monospace hero text (ALL CAPS) over a
 * subtle subtext, framed by the standard `<Panel>` chrome at a wider
 * width than the side panels. Severity drives the accent stripe + hero
 * text color (alert = danger token, warn = warn token, info = text).
 *
 * The flash is anchored top-center, below the metadata strip but
 * above the 3D action. It doesn't animate in/out here — the parent
 * uses CSS `transition` on opacity by toggling `key` on the wrapper
 * so React replaces the element when the accolade changes.
 */

import type { CSSProperties } from "react";
import { Panel } from "./Panel";
import type { Accolade, AccoladeSeverity } from "./narrative";

export interface AccoladeFlashProps {
  accolade: Accolade | null;
  /** Top offset in px (typically below the metadata strip). */
  topOffset?: number;
  /** Hide entirely (overrides `accolade !== null`). */
  hidden?: boolean;
}

const wrapperStyle = (topOffset: number, visible: boolean): CSSProperties => ({
  position: "absolute",
  top: topOffset,
  left: "50%",
  transform: visible
    ? "translate(-50%, 0)"
    : "translate(-50%, -16px)",
  width: "var(--pp-accolade-w)",
  pointerEvents: "none",
  userSelect: "none",
  opacity: visible ? 1 : 0,
  transition: "opacity 280ms ease, transform 280ms ease",
});

const contentStyle: CSSProperties = {
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  textAlign: "center",
};

const heroStyle = (severity: AccoladeSeverity): CSSProperties => ({
  fontSize: "var(--pp-font-hero)",
  fontWeight: "var(--pp-weight-bold)",
  letterSpacing: "var(--pp-tracking-caps)",
  textTransform: "uppercase",
  color: severityColor(severity),
});

const subStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  letterSpacing: "var(--pp-tracking-caps)",
  textTransform: "uppercase",
};

const sideTagStyle = (side: "A" | "B" | "—"): CSSProperties => ({
  fontSize: "var(--pp-font-sm)",
  fontWeight: "var(--pp-weight-bold)",
  color:
    side === "A"
      ? "var(--pp-accent-a)"
      : side === "B"
        ? "var(--pp-accent-b)"
        : "var(--pp-text-muted)",
  letterSpacing: "var(--pp-tracking-caps)",
});

function severityColor(s: AccoladeSeverity): string {
  switch (s) {
    case "alert":
      return "var(--pp-danger)";
    case "warn":
      return "var(--pp-warn)";
    case "info":
      return "var(--pp-text)";
  }
}

function accentFor(side: "A" | "B" | "—", severity: AccoladeSeverity): string {
  if (severity === "alert") return "var(--pp-danger)";
  if (severity === "warn") return "var(--pp-warn)";
  if (side === "A") return "var(--pp-accent-a)";
  if (side === "B") return "var(--pp-accent-b)";
  return "var(--pp-text-muted)";
}

/**
 * Default top offset = strip height (40px) + stack gap (12px) = 52px,
 * matching what `MatchOverlay` computes when the metadata strip is
 * mounted. Audit fix — was previously 60, which didn't line up with
 * anything else.
 */
const DEFAULT_TOP_OFFSET = 52;

export function AccoladeFlash({
  accolade,
  topOffset = DEFAULT_TOP_OFFSET,
  hidden = false,
}: AccoladeFlashProps) {
  const visible = !hidden && accolade !== null;
  // Render a stable element when fading in/out so the CSS transition
  // works on opacity/transform. Keep the most-recent accolade content
  // sticky for fade-out by using key to drive React replacement only
  // when the accolade id actually changes.
  if (!accolade) return null;
  return (
    <div
      key={accolade.id}
      style={wrapperStyle(topOffset, visible)}
      aria-live="polite"
    >
      <Panel variant="solid" accent={accentFor(accolade.side, accolade.severity)}>
        <div style={contentStyle}>
          <span style={sideTagStyle(accolade.side)}>{accolade.side}</span>
          <span style={heroStyle(accolade.severity)}>{accolade.label}</span>
          <span style={subStyle}>{accolade.subtext}</span>
        </div>
      </Panel>
    </div>
  );
}
